import React, { useCallback, useMemo } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useController } from 'react-hook-form';
import Text from '@goodfynd/react-web.typography.text';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
} from '@mui/material';

import { StyledContainer } from '../styles';
import type { InputCheckboxListProps } from '../types';

import { StyledLabel } from './styles';

export default function InputCheckboxList<T extends FieldValues>({
  control,
  label,
  name,
  options,
  max = 0,
  ...props
}: InputCheckboxListProps<T>) {
  const {
    field: { value, onChange },
  } = useController({ control, name });
  const selectedOptions = useMemo(
    () => (value || []) as InputOption[],
    [value]
  );
  const handleSelect = useCallback(
    (selectedOption: InputOption) => {
      const alreadySelected = selectedOptions.find(
        (option) => option.value === selectedOption.value
      );

      if (alreadySelected) {
        const remaining = selectedOptions.filter(
          (item: InputOption) => item.value !== selectedOption.value
        );
        onChange(remaining);
      } else if (max === 0 || selectedOptions.length !== max) {
        onChange(() => [...selectedOptions, selectedOption]);
      } else {
        //TODO: Show max reached message in snackbar?
      }
    },
    [onChange, selectedOptions, max]
  );
  return (
    <StyledContainer {...props}>
      <FormControl fullWidth variant="outlined">
        {label &&
          (typeof label === 'string' ? (
            <FormLabel component="legend">{label}</FormLabel>
          ) : (
            label
          ))}

        {options.map((option) => {
          return (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  onChange={() => handleSelect(option)}
                  checked={
                    !!selectedOptions?.find(
                      (selectedOption) => option.value === selectedOption.value
                    )
                  }
                />
              }
              label={
                <StyledLabel>
                  <Text type="body1">{option.label}</Text>
                  <Text>{option.labelRight}</Text>
                </StyledLabel>
              }
            />
          );
        })}
      </FormControl>
    </StyledContainer>
  );
}
